import { Component, EventEmitter, Inject, Input, NgZone, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CustomGenericTableComponent } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  ClientEnvironment,
  CommonUtilityLib,
  ENVIRONMENT,
  Preferences,
  TableColumn,
  UserPreferencesService,
  ValueListsService,
  ViewType,
} from '@thehubz/client/core';
import { Collection } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { Subject, Subscription, filter, from, interval, map, pairwise, takeUntil, throttleTime } from 'rxjs';
import { AssetCommonService } from '../../../../services/asset-common.service';
import { CollectionEventsService } from '../../services/collection-events.service';
import { VorwerkTableColumnData } from './customer/vorwerk/table-column-data';
import { TableColumnData } from './table-column-data';

@Component({
  selector: 'thehubz-collection-list-view',
  templateUrl: './collection-list-view.component.html',
  styleUrls: ['./collection-list-view.component.scss'],
})
export class CollectionListViewComponent {
  @Input() collectionList!: Array<Collection | Record<string, AnyType>>;
  @Input() collectionCount!: number;
  @Input() filterObject: AnyType = {};
  @Output() selectCollection = new EventEmitter<AnyType>();
  @Input() size = 4;

  @Input() checkedAll? = false;
  @Input() loading? = false;
  @Input() offset!: number;
  @Input() tableType = 'asset';
  @Input() gridSettingsKeySuffix = '';
  @Input() assetUploadStepperFlag?: boolean;

  @Input() selectedViewType!: ViewType;
  @Input() selectedViewSection2: ViewType = 'sortByLastCreated';
  @Input() sortByNew!: boolean;
  @Input() assetCount!: number;

  @Output() changeView = new EventEmitter<any>();
  @Output() sortBy = new EventEmitter<any>();
  @Output() clickSortFilter = new EventEmitter<any>();
  @Output() tableScrolled = new EventEmitter<any>();
  @Output() updateSelectedData = new EventEmitter<any>();
  @Output() actionButton = new EventEmitter<{ action: string; id: string }>();
  @Output() updateCollection = new EventEmitter<AnyType>();

  @ViewChild(CustomGenericTableComponent) customGenericTable!: CustomGenericTableComponent;

  tableColumnData = TableColumnData;
  dataViewFlag = false;
  columns!: TableColumn[];
  additionalColumns!: TableColumn[];
  groupedViewFlag = false;
  selectedTabKey = 'collectionsListView';
  selectedTableColumnSubscription?: Subscription;
  selectedData: AssetUIModel[] = [];
  itemSize = 100;
  httpCallInProgress = false;
  scrollFlag = true;
  tableOffset = 0;
  CustomClients = CustomClients;
  brandingKey!: CustomClients;
  collectionListInfo!: Array<Collection | Record<string, AnyType>>;

  configObject = {
    hideDownload: false,
    hideAddToBasket: true,
    hideAddToCollection: true,
    hideSelection: true,
  };

  renderedAssetRange = { start: 0, end: 0 };

  private destroy$ = new Subject<void>();

  private scrollerSubscription?: Subscription;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private userPreferences: UserPreferencesService,
    private ngZone: NgZone,
    protected assetCommonService: AssetCommonService,
    private router: Router,
    private valueListsService: ValueListsService,
    private commonUtilityLib: CommonUtilityLib,
    private collectionEventsService: CollectionEventsService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
    this.selectedTableColumnSubscription = this.userPreferences.selectedTableColumn.subscribe((data) => {
      if (data && data.changeType === 'collectionList') {
        this.columns = data.defaultColumn;
        this.additionalColumns = data.additionalColumn;
        const collectionColumns: Preferences = { defaultColumns: this.columns, additionalColumns: this.additionalColumns };
        if (data.resetViewFlag) {
          this.userPreferences.clearUserPreferences(this.selectedTabKey, 'asset', 'resetColumns');
        } else {
          this.userPreferences.setUserPreferences(this.selectedTabKey, 'asset', collectionColumns);
        }
        this.collectionEventsService.collectionsTableDataSubject$.next({ columns: this.columns, additionalColumns: this.additionalColumns });
      }
    });
    if (this.brandingKey === CustomClients.Vorwerk) {
      this.tableColumnData = VorwerkTableColumnData;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['collectionList']?.currentValue?.length) {
      this.updateTags();
      this.addAdditionalColumnsData();
    }
  }

  ngOnInit() {
    this.addAdditionalColumnsData();
    this.fetchColumns();
    this.selectedViewType = 'list';
  }
  /**
   * Replace tag ids with respective tag name and update the asset tags
   */
  updateTags() {
    for (const collection of this.collectionList) {
      if (collection.keywords?.length) {
        // might be already updated and tags are Observable<string>[]
        if (collection.keywords[0] && typeof collection.keywords[0] === 'string') {
          collection.keywords = collection.keywords.map((tagId: string) => {
            return this.valueListsService.lookupTagName(tagId);
          });
        }
      }
    }
  }

  getCollection(id: string) {
    const collection = this.collectionList.filter((collection: AnyType) => collection._id === id)[0];
    return collection;
  }

  ngAfterViewInit(): void {
    const newInterval = interval(100).subscribe(() => {
      if (!this.customGenericTable) return;
      if (this.customGenericTable?.scroller) {
        newInterval.unsubscribe();
        this.scrollerSubscription = this.customGenericTable.scroller
          .elementScrolled()
          .pipe(
            map(() => this.customGenericTable.scroller.measureScrollOffset('bottom'), this.customGenericTable.scroller.measureScrollOffset('top')),
            pairwise(),
            filter(([y1, y2]) => y2 < y1 && y2 < 100),
            throttleTime(500),
          )
          .subscribe(() => {
            this.ngZone.run(() => {
              this.tableOffset += 1;
              let newOffset = 0;
              if (this.tableOffset > this.offset) {
                newOffset = this.tableOffset;
                const skip = this.filterObject['limit'] * newOffset;
                this.filterObject['skip'] = this.collectionCount > skip ? skip : 0;
                if (this.filterObject['skip'] > 0) {
                  this.tableScrolled.emit(newOffset);
                }
              }
            });
          });
      }
    });
  }
  /**
   * Fetch tab specific columns based on the tab key
   */
  private fetchColumns() {
    from(this.userPreferences.tabTableConfig(this.selectedTabKey, this.tableType))
      .pipe(takeUntil(this.destroy$))
      .subscribe((config) => {
        const tabTableConfig = config;

        this.columns = tabTableConfig && tabTableConfig.defaultColumns ? tabTableConfig.defaultColumns || [] : this.tableColumnData.TABLE_DEFAULT_COLUMNS;
        this.additionalColumns = tabTableConfig && tabTableConfig.additionalColumns ? tabTableConfig.additionalColumns || [] : this.tableColumnData.ADD_COLUMNS;
        // Arval changes to the asset list view
        // hide agency client brand columns
        if (this.brandingKey === CustomClients.Arval) {
          this.columns = this.commonUtilityLib.hideColumn(['client', 'brand'], this.columns);
          this.additionalColumns = this.commonUtilityLib.hideColumn(['agency'], this.additionalColumns);
        }
        this.collectionEventsService.collectionsTableDataSubject$.next({ columns: this.columns, additionalColumns: this.additionalColumns });
      });
  }

  private addAdditionalColumnsData() {
    this.collectionList.forEach((collection: AnyType) => {
      if (collection.published.length) {
        collection.agency = collection.published.map((item: { name: any }) => ' ' + item.name);
        collection.client = [
          ...new Set(collection.published.map((item: { clients: any }) => item.clients.map((client: { name: any }) => ' ' + client.name)).flat(1)),
        ];
        collection.brand = [
          ...new Set(
            collection.published
              .map((item: { clients: any }) =>
                item.clients.map((client: { brands: any }) => ' ' + client.brands.map((brand: { name: string }) => ' ' + brand.name)),
              )
              .flat(1),
          ),
        ];
      }
    });
  }

  gotoAssetList(collectionId: string) {
    this.router.navigate(['collections/collection-asset-list/' + collectionId]);
  }

  onEditCollection(id: string) {
    this.actionButton.emit({ action: 'editCollection', id });
  }

  onDownloadCollection(id: string) {
    this.actionButton.emit({ action: 'downloadCollection', id });
  }

  onDeleteCollection(id: string) {
    this.actionButton.emit({ action: 'deleteCollection', id });
  }

  onShareCollection(id: string) {
    this.actionButton.emit({ action: 'shareCollection', id });
  }

  onPublishCollection(id: string) {
    this.actionButton.emit({ action: 'publishCollection', id });
  }

  onUnpublishCollection(id: string) {
    this.actionButton.emit({ action: 'unpublishCollection', id });
  }

  onSelectCollection(id: string) {
    this.selectCollection.emit(id);
  }

  onClickSortOption() {
    this.sortByNew = !this.sortByNew;
    this.sortBy.emit();
  }

  onClickSortFilter(event: Event) {
    this.clickSortFilter.emit(event);
  }

  ngOnDestroy(): void {
    this.selectedTableColumnSubscription && this.selectedTableColumnSubscription.unsubscribe();
    this.scrollerSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
